import { ReactNode } from "react";

import { DragEndEvent } from "@dnd-kit/core";
import { Box, Stack } from "@mui/material";

import { QuestionCard } from "./QuestionCard/QuestionCard";
import { SortableQuestion, SortableQuestionCards } from "./SortableQuestionCards";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { Add, Location, Mail, Success, TextInput } from "@/icons/outlined";

interface DonationLinkedQuestionsListV4Props<TQuestion extends SortableQuestion> {
  questions: TQuestion[];
  onReorder: (event: DragEndEvent) => void;
  onEdit: (question: TQuestion) => void;
  onDelete: (localId: string) => void;
  onAddQuestion: () => void;
  askForAddress: boolean;
  onDeleteAddress: () => void;
  additionalOptions?: ReactNode;
  trailingOptions?: ReactNode;
}

export const DonationLinkedQuestionsListV4 = <TQuestion extends SortableQuestion>({
  questions,
  onReorder,
  onEdit,
  onDelete,
  onAddQuestion,
  askForAddress,
  onDeleteAddress,
  additionalOptions,
  trailingOptions,
}: DonationLinkedQuestionsListV4Props<TQuestion>) => {
  const { t } = useTranslate();

  const isReorderable = questions.length > 1;

  return (
    <Stack spacing={1.5}>
      <Stack spacing={1}>
        <QuestionCard
          icon={<TextInput />}
          label={t("dashboard", "fullName")}
          subtitle={t("dashboard", "stepQuestionsV4.typeShortText")}
          required
          showDisabledReorderHandle={isReorderable}
          deleteDisabledTooltip={t("dashboard", "stepQuestionsV4.defaultQuestionTooltip")}
        />
        <QuestionCard
          icon={<Mail />}
          label={t("dashboard", "email")}
          subtitle={t("dashboard", "donationForm.questionEmail")}
          required
          showDisabledReorderHandle={isReorderable}
          deleteDisabledTooltip={t("dashboard", "stepQuestionsV4.defaultQuestionTooltip")}
        />
        {askForAddress ? (
          <QuestionCard
            icon={<Location />}
            label={t("dashboard", "fullAddress")}
            subtitle={t("dashboard", "stepQuestionsV4.dialog.typeAddress")}
            required
            showDisabledReorderHandle={isReorderable}
            onDelete={onDeleteAddress}
          />
        ) : (
          <QuestionCard
            icon={<Success />}
            label={t("dashboard", "stateAndCountry")}
            subtitle={t("dashboard", "donationForm.uniqueQuestion")}
            required
            showDisabledReorderHandle={isReorderable}
            deleteDisabledTooltip={t("dashboard", "stepQuestionsV4.defaultQuestionTooltip")}
          />
        )}

        {additionalOptions}

        <SortableQuestionCards questions={questions} onReorder={onReorder} onEdit={onEdit} onDelete={onDelete} />

        {trailingOptions}
      </Stack>

      <Box>
        <DashboardButton
          variant="outlined"
          vibe="brand"
          startIcon={<Add />}
          onClick={onAddQuestion}
          data-test="v4-add-donation-question-button"
        >
          {t("dashboard", "stepQuestionsV4.addQuestion")}
        </DashboardButton>
      </Box>
    </Stack>
  );
};
